
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import ContentAdaptLayout from '../../../../_components/content-adapt-layout/content-adapt-layout.vue'

import {
  GroupPolicySettingsService,
  GroupPolicyTypes,
  IntervalTypes,
  TimingAntivirusSettings,
  ScanTaskTypes,
  ScanTypes
} from '../../group-policy-settings.service'

import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { useI18n } from 'vue-i18n'
import { OperateType } from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.service'
import DetailsSubscribeBar from '@/views/dashboard/_components/subscribe-bar/details-subscribe-bar.vue'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { BrandConfig, useBrandCustomStore } from '@/services/brand-custom.service'
import TaskTimeEdit from '@/views/dashboard/_components/task-time/task-time-edit.vue'
import { DefaultTaskTime } from '@/views/dashboard/_components/task-time/task-time.service'
const DEFAULT_PARAMS = {
  main_switch: true,
  scan_type: 0,
  task_type: 0,
  interval_type: 3,
  frequency: 1,
  regular_tasks: [],
}

const SystemTimingAntivirusTypes = {
  1: GroupPolicyTypes.TimingAntivirus,
  2: GroupPolicyTypes.LinuxTimingAntivirus,
  4: GroupPolicyTypes.MacTimingAntivirus
}

export default defineComponent({
  components: {
    DetailsSubscribeBar,
    ContentAdaptLayout,
    TaskTimeEdit,
  },
  props: {
    operateType: {
      type: String as PropType<'edit'|'add'|'read'>,
      default: 'edit'
    },
    policyId: {
      type: String,
      default: undefined
    },
    name: {
      type: String,
      default: ''
    },
    activeName: {
      type: String,
      default: ''
    },
    systemTab: {
      type: String,
      default: '1'
    }
  },
  setup (props, context) {
    const { t, locale } = useI18n()
    const injector = useReflectiveInjector()
    const groupPolicySettingsService = injector.get(GroupPolicySettingsService)
    const servicePermissionService = injector.get(ServicePermissionService)

    const loading = ref<boolean>(true)
    const dialogDemoVisible = ref<boolean>(false)

    const strategy = reactive<TimingAntivirusSettings>({
      ...DEFAULT_PARAMS
    })
    const data = reactive({
      allScanTip: false,
      hasEdit: false
    })

    const quickScanImages: Record<string, string> = {
      'zh-CN': require('../_assets/zh-CN/quick_scan.png'),
      'zh-TW': require('../_assets/zh-TW/quick_scan.png'),
      'en-US': require('../_assets/en-US/quick_scan.png'),
      'ja-JP': require('../_assets/ja-JP/quick_scan.png'),
    }
    const threeMinScanImages: Record<string, string> = {
      'zh-CN': require('../_assets/zh-CN/3min_scan.png'),
      'zh-TW': require('../_assets/zh-TW/3min_scan.png'),
      'en-US': require('../_assets/en-US/3min_scan.png'),
      'ja-JP': require('../_assets/ja-JP/3min_scan.png'),
    }

    const quickScanImage = computed(() => quickScanImages[locale.value] || quickScanImages['zh-CN'])
    const threeMinScanImage = computed(() => threeMinScanImages[locale.value] || threeMinScanImages['zh-CN'])

    const hasPermission = computed(() => {
      if (+props.systemTab === TerminalType.linux) {
        return servicePermissionService.getHasPermission(PermissionFe.linux_base_auth)
      }
      return true
    })

    function getSettings () {
      groupPolicySettingsService.getSettings({
        id: props.policyId,
        type: SystemTimingAntivirusTypes[props.systemTab]
      }).then(response => {
        const settingsData = convertOldDataToNew(response.strategy as TimingAntivirusSettings)
        Object.assign(strategy, {
          ...DEFAULT_PARAMS
        }, settingsData)
        loading.value = false
      }).finally(() => {
        loading.value = false
      })
    }

    /** 新老数据适配器 */
    function convertOldDataToNew (data: TimingAntivirusSettings) {
      data.regular_tasks.forEach(v => {
        v.extra1 = v.extra1 ?? data.scan_type
        v.extra2 = v.extra2 ?? data.frequency
      })
      data.scan_type = undefined
      data.frequency = undefined
      return data
    }

    function saveSettings () {
      groupPolicySettingsService.saveSettings({
        id: props.policyId,
        type: SystemTimingAntivirusTypes[props.systemTab],
        strategy: JSON.stringify(strategy)
      }).then(() => {
        groupPolicySettingsService.updateEditTab(false)
        context.emit('save')
      })
    }
    function cancel () {
      context.emit('cancel')
    }
    groupPolicySettingsService.hasEditTab.subscribe((hasEdit: boolean) => {
      data.hasEdit = hasEdit
    })
    groupPolicySettingsService.saveTab.subscribe((name: string) => {
      if (name === 'timing-antivirus') {
        saveSettings()
      }
    })
    watch(() => props.systemTab, () => {
      getSettings()
    })
    watch(() => props.activeName, (newV) => {
      if (newV === 'timing-antivirus') {
        getSettings()
      }
    })
    watch(() => strategy.regular_tasks.length, (length) => {
      if (length > 1) {
        strategy.interval_type = 0
      }
    })
    getSettings()
    return {
      t,
      TerminalType,
      PermissionFe,
      OperateType,
      brandConfig: useBrandCustomStore().brandConfig as BrandConfig,
      hasPermission,
      loading,
      strategy,
      ScanTypes,
      ScanTaskTypes,
      IntervalTypes,
      dialogDemoVisible,
      data,
      quickScanImage,
      threeMinScanImage,
      cancel,
      handlerShowDialogDemo (flag: boolean) {
        data.allScanTip = flag
        dialogDemoVisible.value = true
      },
      handlerSave () {
        saveSettings()
      },
      setChangeStatus () {
        groupPolicySettingsService.updateEditTab(true)
      },
      addTaskTime (index) {
        groupPolicySettingsService.updateEditTab(true)
        strategy.regular_tasks[index].extra1 = ScanTypes.Lightning
        strategy.regular_tasks[index].extra2 = 1
      },
      resetRowTime (index) {
        groupPolicySettingsService.updateEditTab(true)
        strategy.regular_tasks[index].target = 1
      }
    }
  }
})
